// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, MessageCircle, Package, Calendar, Clock, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import OrderDetailsModal from './OrderDetailsModal';

interface VideoOrder {
  id: string;
  vendor_id: string;
  event_id: string;
  package_id: string;
  user_id: string;
  purchaser_name: string;
  purchaser_email: string;
  purchaser_phone: string;
  player_name: string;
  total_amount_cents: number;
  status: string;
  created_at: string;
  updated_at: string;
  team_name?: string;
  notes?: string;
  video_url?: string;
  // Joined data
  vendor?: {
    business_name: string;
    user_id: string;
    email?: string;
  };
  event?: {
    name: string;
    start_date: string;
  };
  package?: {
    package_type: string;
    price_cents: number;
    description: string;
  };
}

const CustomerVideoOrders: React.FC = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<VideoOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<VideoOrder | null>(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  useEffect(() => {
    if (user) {
      fetchCustomerOrders();
    }
  }, [user]);

  const fetchCustomerOrders = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('video_orders')
        .select(`
          *,
          vendor:video_vendor_profiles!vendor_id(business_name, user_id),
          event:events!event_id(name),
          package:video_packages!package_id(package_type)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setOrders(data || []);
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  };

  const openMessageThread = (order: VideoOrder) => {
    console.log('Opening order details for:', order);
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading your orders...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Your Video Orders
          </CardTitle>
        </CardHeader>
        <CardContent>
          {orders.length > 0 ? (
            <div className="space-y-4">
              {orders.map((order) => (
                <Card key={order.id} className="border border-border/50">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{order.event?.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">{order.package?.package_type}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            Vendor: {order.vendor?.business_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            Player: {order.player_name}
                          </span>
                        </div>
                      </div>
                      
                      <div className="text-right space-y-2">
                        <Badge className={getStatusColor(order.status)}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </Badge>
                        <p className="text-sm font-medium">{formatPrice(order.total_amount_cents)}</p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {formatDate(order.created_at)}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-3 border-t">
                      <p className="text-xs text-muted-foreground">
                        Order #{order.id.substring(0, 8)}...
                      </p>
                       <div className="flex gap-2">
                         <Button
                           variant="outline"
                           size="sm"
                           onClick={() => openMessageThread(order)}
                           className="flex items-center gap-2"
                         >
                           <Info className="h-4 w-4" />
                           View Details
                         </Button>
                       </div>
                    </div>

                    {/* Order Status Info */}
                    <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                      <div className="text-sm">
                        {order.status === 'pending' && (
                          <p className="text-yellow-700">
                            Your order is pending confirmation from the vendor.
                          </p>
                        )}
                        {order.status === 'confirmed' && (
                          <p className="text-blue-700">
                            Your order has been confirmed! The vendor will contact you with details.
                          </p>
                         )}
                         {order.status === 'completed' && (
                           <p className="text-green-700">
                             Your video order has been completed! Click "View Details" to see the video and request revisions if needed.
                           </p>
                         )}
                         {order.status === 'revision_requested' && (
                           <p className="text-orange-700">
                             You have requested revisions for this video. The vendor will update the video based on your feedback.
                           </p>
                         )}
                         {order.status === 'cancelled' && (
                          <p className="text-red-700">
                            This order has been cancelled.
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Video Orders</h3>
              <p className="text-muted-foreground">
                You haven't placed any video orders yet. Browse events to find video vendors.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Details Modal */}
      <OrderDetailsModal
        open={showOrderDetails}
        onOpenChange={setShowOrderDetails}
        order={selectedOrder}
        onOrderUpdate={() => {
          fetchCustomerOrders();
        }}
      />
    </>
  );
};

export default CustomerVideoOrders;